// Extracted from BatucadaRio.jsx — ScriptEditorTab

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Tone from 'tone';
import {
  Play, Pause, Square, Volume2, Search, Download, Settings, ArrowLeft,
  Drum, Clock, SkipBack, Repeat, Layers, ChevronLeft, ChevronRight,
  Sliders, Grid3X3, FileAudio, Code, Package, FolderUp, Save, ChevronUp, ChevronDown,
  Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// SCRIPT EDITOR TAB (Placeholder)
// ============================================
export const ScriptEditorTab = ({ sample, selectedKey, settings, onTrimChange }) => {
  const buildDefaultScript = () => `on init
  { Kontakt Script for ${sample?.name || 'Sample'} }
  { Supported commands (inside on note): set_volume(dB -24..24), set_pan(-100..100), set_tune(semitones -12..12), set_group(1..4) }
end on

on note
  set_volume(0)
  set_pan(0)
  set_tune(0)
  set_group(${settings?.group || 1})
end on`;

  const [script, setScript] = useState(settings?.script || buildDefaultScript());
  const [output, setOutput] = useState([]);

  useEffect(() => {
    setScript(settings?.script || buildDefaultScript());
    setOutput([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const compile = () => {
    const lines = [];
    const src = script.replace(/\{[^}]*\}/g, ' ');
    const opens = (src.match(/\bon\s+(init|note|release|controller)\b/g) || []).length;
    const closes = (src.match(/\bend\s+on\b/g) || []).length;
    if (opens === 0) {
      setOutput(['ERROR: No callback blocks found. Add "on note ... end on".']);
      return;
    }
    if (opens !== closes) {
      setOutput([`ERROR: ${opens} "on ..." block(s) but ${closes} "end on". Blocks must match.`]);
      return;
    }

    const patch = { ...(settings || {}), script };
    const grab = (name) => {
      const m = src.match(new RegExp(name + '\\s*\\(\\s*(-?\\d+(?:\\.\\d+)?)\\s*\\)'));
      return m ? parseFloat(m[1]) : null;
    };
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    const vol = grab('set_volume');
    if (vol !== null) { patch.gainDb = clamp(vol, -24, 24); lines.push(`set_volume -> ${patch.gainDb} dB`); }
    const pan = grab('set_pan');
    if (pan !== null) { patch.pan = clamp(pan, -100, 100); lines.push(`set_pan -> ${patch.pan}`); }
    const tune = grab('set_tune');
    if (tune !== null) { patch.tune = clamp(tune, -12, 12); lines.push(`set_tune -> ${patch.tune} st`); }
    const grp = grab('set_group');
    if (grp !== null) { patch.group = clamp(Math.round(grp), 1, 4); lines.push(`set_group -> Group ${patch.group}`); }

    onTrimChange(selectedKey, patch);
    lines.push(`Compiled OK. Applied to ${selectedKey}.`);
    setOutput(lines);
  };

  const clearScript = () => {
    const fresh = buildDefaultScript();
    setScript(fresh);
    setOutput(['Script reset to default (not yet compiled).']);
  };

  return (
    <div className="space-y-3" data-testid="script-editor">
      <div className="bg-[#111] rounded-lg p-2 font-mono">
        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          className="w-full h-32 bg-transparent text-green-400 text-xs resize-none focus:outline-none"
          spellCheck="false"
          data-testid="script-editor-textarea"
        />
      </div>
      <div className="flex gap-2 items-start">
        <button
          onClick={compile}
          className="px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded hover:bg-amber-400"
          data-testid="script-compile-btn"
        >
          Compile
        </button>
        <button
          onClick={clearScript}
          className="px-3 py-1 bg-[#333] text-white text-xs rounded hover:bg-[#444]"
          data-testid="script-clear-btn"
        >
          Clear
        </button>
        {output.length > 0 && (
          <div className="flex-1 bg-[#111] rounded p-2 font-mono" data-testid="script-output">
            {output.map((l, i) => (
              <div key={i} className={`text-[10px] ${l.startsWith('ERROR') ? 'text-red-400' : 'text-green-400'}`}>{l}</div>
            ))}
          </div>
        )}
      </div>
      <p className="text-[10px] text-gray-500 text-center">
        Script for: {sample?.name || 'None'} ({selectedKey}) — compiled settings apply to playback
      </p>
    </div>
  );
};
